import { mergeRowResponseFromTop } from 'owa-mail-list-response-processor';
import type { MailFolderTableQuery, MailListRowDataType, TableView } from 'owa-mail-list-store';
import {
    type TableQueryType,
    canTableBeOutOfSyncWithServer,
    isPriorityView,
} from 'owa-mail-list-store';
import type FolderId from 'owa-service/lib/contract/FolderId';
import { mutatorAction } from 'satcheljs';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { lazyFetchReactionDataForMessageList } from 'owa-listview-reactions';
import type { PagingInfoInput as GqlPagingInfo } from 'owa-graph-schema';
import { setCurrentLoadedIndexMutator } from './setCurrentLoadedIndexMutator';

const serverFolderIdMutator = mutatorAction(
    'serverFolderIdMutator',
    (tableView: TableView, serverFolderId: string) => {
        tableView.serverFolderId = serverFolderId;
    }
);

const loadedStartIndexMutator = mutatorAction(
    'loadedStartIndexMutator',
    (tableView: TableView, index: number) => {
        tableView.loadedStartIndex = index;
    }
);

/**
 * Callback when load initial rows request succeeds
 * @param tableView to load
 * @param newRows to be added
 * @param totalRowsInView the number of total rows in the view/folder
 * @param indexOfLastRowInView the index of the last row in view
 * @param searchFolderId the searchFolderId contained in the response message
 */
export default function onLoadInitialRowsSucceeded(
    tableView: TableView,
    responseRows: MailListRowDataType[],
    totalRowsInView: number,
    indexOfLastRowInView: number,
    searchFolderId?: FolderId,
    folderId?: FolderId,
    pagingInfo?: GqlPagingInfo,
    hasNextPage: boolean = false
): void {
    // In case of groups, we don't initially have the actual folder id when the tableView is created (similar to search folders in inbox),
    // So we get it in the response for findConversation call and update the serverFolderId.
    if (
        (tableView.tableQuery.type == 2 ||
            (isGlobalFeatureEnabled('grp-loadFolders') && tableView.tableQuery.type == 3)) &&
        folderId
    ) {
        serverFolderIdMutator(tableView, folderId.Id);
    }

    // If SearchFolderId is returned in response, set it on the tableView
    if (searchFolderId) {
        serverFolderIdMutator(tableView, searchFolderId.Id);
    }

    // Invalidate the remaining rows if the client table is out of sync with the server
    const shouldRemoveRemainingRowsAfterMerge = canTableBeOutOfSyncWithServer(tableView);

    // Update rows in listview store
    // also log isDataUptodate only if the table is not being loaded for first time
    mergeRowResponseFromTop(
        responseRows,
        tableView,
        totalRowsInView,
        shouldRemoveRemainingRowsAfterMerge /* removeRemainingRowsAfterMerge */,
        tableView.isInitialLoadComplete /* shouldLogIsDataUptodate */,
        'MergeOnInitialRowsFetch',
        false /* isManualSync */,
        false /* showNotification */,
        hasNextPage,
        true /* shouldHonorTombstonedReadStateOnMerge */
    );

    setCurrentLoadedIndexMutator(tableView);

    // Set the loaded start index to be the last element index - the number of rows
    // The number of rows is the same as tableView.rowKeys.length since it is the initial load
    if (pagingInfo?.pageOrigin === 'End') {
        loadedStartIndexMutator(tableView, Math.max(0, totalRowsInView - tableView.rowKeys.length));
    } else if (isPriorityView(tableView.tableQuery as MailFolderTableQuery)) {
        // We always load from the top of the table in Priority View. Additionally,
        // because of the client-side filtering that's done on the table, the number
        // of rows in the view may be less than the number of rows actually loaded.
        // Therefore, we set the loadedStartIndex to 0.
        loadedStartIndexMutator(tableView, 0);
    } else {
        loadedStartIndexMutator(
            tableView,
            Math.max(0, indexOfLastRowInView - tableView.rowKeys.length)
        );
    }

    // Iterate over new rows and determine if any of them have a non-zero
    // ReactionsCount property. If so, add them to the targetRowKeys array
    // for the fetchReactionDataForMessageList orchestrator to process.
    const targetRowKeys: string[] = [];

    responseRows.forEach((row: MailListRowDataType) => {
        if ((row.ReactionsCount ?? 0) > 0 && row.InstanceKey) {
            targetRowKeys.push(row.InstanceKey);
        }
    });

    if (targetRowKeys.length > 0) {
        lazyFetchReactionDataForMessageList.importAndExecute(tableView, targetRowKeys);
    }
}
